import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { VideoAnalysisSection } from "./VideoAnalysisSection";
import { PhotosTab } from "./PhotosTab";
import { Camera, Video, Scan } from "lucide-react";
import { FieldScout } from "@/components/mobile/FieldScout";
import { FieldScoutReview } from "@/components/mobile/FieldScoutReview";
import { ScoutBookmark } from "@/hooks/useFieldScout";
import { useVideoAnalysis } from "@/hooks/useVideoAnalysis";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface DamageHubTabProps {
  jobId: string;
  orgId: string;
  photos: any[];
  onPhotosUpdate: () => void;
  onFindingsUpdate: () => void;
}

export const DamageHubTab = ({ 
  jobId, 
  orgId, 
  photos, 
  onPhotosUpdate,
  onFindingsUpdate 
}: DamageHubTabProps) => {
  const [showFieldScout, setShowFieldScout] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [scoutVideo, setScoutVideo] = useState<Blob | null>(null);
  const [scoutBookmarks, setScoutBookmarks] = useState<ScoutBookmark[]>([]);
  const { uploadVideo, extractBookmarkedFrames } = useVideoAnalysis();
  const [analyzing, setAnalyzing] = useState(false);

  const handleScoutComplete = (videoBlob: Blob, bookmarks: ScoutBookmark[]) => {
    setScoutVideo(videoBlob);
    setScoutBookmarks(bookmarks);
    setShowFieldScout(false);
    setShowReview(true);
  };

  const handleReviewConfirm = async (confirmedBookmarks: ScoutBookmark[]) => {
    if (!scoutVideo) return;

    setAnalyzing(true);
    setShowReview(false);

    try {
      // Upload video
      const uploadResult = await uploadVideo(scoutVideo, jobId, orgId, `field-scout-${Date.now()}.webm`);
      
      // Extract frames at bookmarked timestamps
      const { frames, metadata } = await extractBookmarkedFrames(uploadResult.url, confirmedBookmarks);
      
      toast.success(`Extracted ${frames.length} frames from marked spots`);

      // Analyze each frame
      for (let i = 0; i < frames.length; i++) {
        const frame = frames[i];
        const meta = metadata[i];

        const { error } = await supabase.functions.invoke('analyze-damage', {
          body: {
            image: frame,
            source: 'field_scout',
            context: `Field Scout - ${meta.damageType || 'Unknown'} damage at ${meta.timestamp}s`,
            orgId: orgId
          }
        });

        if (error) {
          console.error('Analysis error:', error);
          toast.error(`Failed to analyze spot ${i + 1}`);
        }
      }

      toast.success('Field Scout analysis complete!');
      onFindingsUpdate?.();
    } catch (error) {
      console.error('Field Scout processing error:', error);
      toast.error('Failed to process Field Scout recording');
    } finally {
      setAnalyzing(false);
      setScoutVideo(null);
      setScoutBookmarks([]);
    }
  };

  if (showFieldScout) {
    return (
      <FieldScout
        onComplete={handleScoutComplete}
        onCancel={() => setShowFieldScout(false)}
      />
    );
  }

  if (showReview && scoutVideo) {
    return (
      <FieldScoutReview
        videoBlob={scoutVideo}
        bookmarks={scoutBookmarks}
        onConfirm={handleReviewConfirm}
        onCancel={() => {
          setShowReview(false);
          setScoutVideo(null);
          setScoutBookmarks([]);
        }}
      />
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 space-y-6">
      {/* Field Scout Tool */}
      <Card className="border-primary/20 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Scan className="w-5 h-5 text-primary" />
            Field Scout
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              Record video inspection and tap to mark damage spots in real-time
            </p>
            <Button 
              size="lg" 
              className="gap-2"
              onClick={() => setShowFieldScout(true)}
              disabled={analyzing}
            >
              <Camera className="w-5 h-5" />
              {analyzing ? 'Analyzing...' : 'Start Field Scout'}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Photo/Video Analysis Section */}
      <Card className="border-primary/20 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-accent/10 to-accent/5 border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Video className="w-5 h-5 text-accent" />
            Photo/Video Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Video Upload Section */}
          <VideoAnalysisSection jobId={jobId} orgId={orgId} />
          
          {/* Photo Gallery Section */}
          <div className="pt-4 border-t">
            <PhotosTab 
              jobId={jobId} 
              photos={photos} 
              onPhotosUpdate={onPhotosUpdate} 
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
